import clsx from "clsx";
import { ForwardedRef, ReactNode, forwardRef } from "react";

import { TagAction } from "../../types/Tag.types";
import { Spinner } from "../Spinner/Spinner";
import { Link } from "../Link";
import { createTagActionClickHandler } from "../../utils/createTagActionClickHandler";

import styles from "./Tile.module.scss";

interface SharedTileProps {
  /** CSS class to apply to the root element */
  className?: string;

  /** Call to action, displayed in blue so it looks like a link (even though the entire tile is clickable) */
  cta: string;

  /** Icon to display */
  icon: React.ReactNode;

  /** Tracking name and action */
  tagAction?: TagAction;

  /** Add additional icon to cta  */
  ctaIcon?: ReactNode;

  /** Display spinner instead of children */
  isLoading?: boolean;

  /** Optional aria-label to apply to the root element */
  ariaLabel?: string;
}

export interface AnchorTileProps extends SharedTileProps {
  /** Href to load when the tile is clicked */
  href: string;

  /** Set to true to open link in new tab */
  newTab?: boolean;

  /** Function to call when the tile is clicked */
  onClick?: () => void;
}

export interface ButtonTileProps extends SharedTileProps {
  /** Button doesn't support an href */
  href?: never;

  /** New tab isn't supported for a button */
  newTab?: never;

  /** Function to call when the tile is clicked */
  onClick: () => void;
}

export const Tile = forwardRef(
  (
    {
      ariaLabel,
      href,
      onClick,
      newTab = false,
      tagAction,
      isLoading,
      cta,
      ctaIcon,
      icon,
      className,
    }: AnchorTileProps | ButtonTileProps,
    ref: ForwardedRef<any>
  ) => {
    const classes = clsx(styles.card, className);

    if (isLoading) {
      return (
        <div className={classes}>
          <div className={styles.spinnerContainer}>
            <Spinner className={styles.spinner} />
          </div>
        </div>
      );
    }

    const children = (
      <>
        <div className={styles.icon}>{icon}</div>
        <div className={styles.cta}>
          {cta}
          {ctaIcon}
        </div>
      </>
    );

    if (href) {
      return (
        <Link
          ref={ref}
          href={href}
          className={classes}
          ariaLabel={ariaLabel || cta}
          newTab={newTab}
          tagAction={tagAction}
          onClick={onClick}
        >
          {children}
        </Link>
      );
    }

    const tagActionClickHandler = createTagActionClickHandler({
      onClick,
      tagAction,
    });

    return (
      <button
        ref={ref}
        className={classes}
        aria-label={ariaLabel || cta}
        onBlur={(event) => event.currentTarget.blur()}
        onClick={(event) => {
          event.currentTarget.focus();
          tagActionClickHandler?.(event);
        }}
      >
        {children}
      </button>
    );
  }
);
